import { needOf } from './evaluateGpb';
import type { GpbDefinition, GpbGoal, GpbPredicate } from '../../types/gpb';

/**
 * ⭐⭐ **WHAT CHANGED BETWEEN TWO PUBLISHED VERSIONS, IN THE GOALS' OWN WORDS.**
 *
 * D26 refuses "a new version is available" as enough to decide with, and asks for
 * the change stated the way the goal states itself — *"Complete seven planned
 * sessions" → "Complete ten planned sessions"*. This computes that, and hole H4
 * is what it closes.
 *
 * ⚠️⚠️ **DERIVED, NOT AUTHORED, AND THAT IS THE WHOLE POINT.** The alternative was
 * a change list written by hand into the release entry. It would have been a
 * second copy of the truth: an author who writes "raised to ten" and ships twelve
 * breaks D26's promise with every gate green, because nothing compares the prose
 * to the definition. Reading both definitions cannot lie about them. D37 is what
 * makes this possible at all — a version stays in the bundle while anybody holds
 * it, so the predecessor is still here to be read.
 *
 * ⚠️⚠️ **A CHANGE MUST NOT BE ABLE TO HIDE.** Three flags, not one, because the
 * ways a goal can change are not interchangeable:
 *  - `words` — the person reads different text for the same requirement.
 *  - `needs` — the counted denominator moved. This is D26's headline case.
 *  - `rules` — everything else about the predicates: kind, `per`, `mode`, `ids`,
 *    `variant`, `after`, `fresh`. ⚠️ `per: 'day'` → `'opportunity'` keeps the same
 *    count and the same sentence while changing what satisfies it — the schema
 *    warns it "would silently let a practice scheduled every other day be
 *    satisfied twice as fast". A diff watching only counts would call that no
 *    change at all.
 *
 * ⚠️ Nothing here decides whether an update is a good idea, offers it, or applies
 * it. It reports. The accept/decline flow is D26's review, and lands with the
 * second release.
 */

/** One side of a comparison — a goal as one version states it. */
export interface GpbGoalSide {
  title: string;
  evidenceLine: string;
  /**
   * What each part asks for, in the goal's authored order.
   * ⚠️ From `needOf`, the evaluator's own denominator — never recomputed here, or
   * a release could announce a number the engine does not enforce.
   */
  needs: readonly number[];
}

export interface GpbGoalChange {
  rank: number;
  questId: string;
  questTitle: string;
  goalId: string;
  before: GpbGoalSide;
  after: GpbGoalSide;
  /** The goal's text moved. */
  words: boolean;
  /** A counted denominator moved — D26's headline case. */
  needs: boolean;
  /** The predicates differ in some way the words and the counts do not show. */
  rules: boolean;
}

/** A goal that exists on only one side. */
export interface GpbGoalPresence {
  rank: number;
  questId: string;
  questTitle: string;
  goalId: string;
  title: string;
}

export interface GpbVersionDiff {
  from: string;
  to: string;
  changed: readonly GpbGoalChange[];
  added: readonly GpbGoalPresence[];
  /**
   * ⚠️⚠️ Surfaced separately and never folded into `changed`. D34 says a profile
   * can never remove what it brought; a release that drops a goal is the one a
   * reviewer must see on its own rather than buried in a list of edits.
   */
  removed: readonly GpbGoalPresence[];
}

/**
 * A stable string for a predicate, independent of the order its keys were typed.
 *
 * ⚠️ `JSON.stringify` follows insertion order, so two identical predicates
 * authored with their keys in a different sequence would compare as different and
 * report a change nobody made. Sorting the keys is what makes "no change" mean it.
 */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * What a goal asks for, by part.
 *
 * ⚠️ Per part rather than summed. A goal with two parts asking 7 and 1 is not a
 * goal asking for 8 — summing would invent a number the person never sees and
 * would hide a change that moved the parts while keeping the total.
 */
const sideOf = (goal: GpbGoal, def: GpbDefinition): GpbGoalSide => ({
  title: goal.title,
  evidenceLine: goal.evidenceLine,
  needs: goal.all.map(p => needOf(p, def)),
});

/** The predicates with every counted denominator stripped out. */
const rulesFingerprint = (all: readonly GpbPredicate[]): string =>
  canonical(all.map(p => {
    /* ⚠️ The counted fields come out because `needs` already reports them. What
       is left is everything that changes MEANING without changing the number. */
    const { ...rest } = p as Record<string, unknown>;
    delete rest.count;
    delete rest.ids;
    return rest;
  }));

const sameNeeds = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((n, i) => n === b[i]);

interface Located { rank: number; questId: string; questTitle: string; goal: GpbGoal }

/** Every goal in a definition, with the quest and rank it sits under. */
function locateGoals(def: GpbDefinition): Map<string, Located> {
  const out = new Map<string, Located>();
  for (const rank of def.ranks) {
    for (const quest of rank.quests) {
      for (const goal of quest.goals) {
        /* ⚠️ Keyed by quest AND goal: a goal id is authored unique within its
           quest, not across the definition. Keying on the goal alone would make
           two quests' `G01` the same goal and diff one against the other. */
        out.set(`${quest.id}/${goal.id}`, {
          rank: rank.rank, questId: quest.id, questTitle: quest.title, goal,
        });
      }
    }
  }
  return out;
}

const presenceOf = (l: Located): GpbGoalPresence => ({
  rank: l.rank, questId: l.questId, questTitle: l.questTitle,
  goalId: l.goal.id, title: l.goal.title,
});

/**
 * Compare two published versions of one profile.
 *
 * ⚠️ `prev` and `next` must be the same profile; comparing two different profiles
 * would report every goal as removed and added, which is true and useless. The
 * registry only ever hands this two versions of one id.
 */
export function diffDefinitions(prev: GpbDefinition, next: GpbDefinition): GpbVersionDiff {
  const before = locateGoals(prev);
  const after = locateGoals(next);

  const changed: GpbGoalChange[] = [];
  const added: GpbGoalPresence[] = [];
  const removed: GpbGoalPresence[] = [];

  for (const [key, b] of after) {
    const a = before.get(key);
    if (!a) { added.push(presenceOf(b)); continue; }

    const beforeSide = sideOf(a.goal, prev);
    const afterSide = sideOf(b.goal, next);
    const words = beforeSide.title !== afterSide.title
      || beforeSide.evidenceLine !== afterSide.evidenceLine;
    const needs = !sameNeeds(beforeSide.needs, afterSide.needs);
    const rules = rulesFingerprint(a.goal.all) !== rulesFingerprint(b.goal.all);

    if (words || needs || rules) {
      changed.push({
        rank: b.rank, questId: b.questId, questTitle: b.questTitle, goalId: b.goal.id,
        before: beforeSide, after: afterSide, words, needs, rules,
      });
    }
  }

  for (const [key, a] of before) {
    if (!after.has(key)) removed.push(presenceOf(a));
  }

  return { from: prev.version, to: next.version, changed, added, removed };
}
